import { FOLLOW_UP_TYPES, FOLLOW_UP_TYPE_LABELS, PAGE_SIZE_OPTIONS, type FollowUpListQuery } from '@chukua/common';
import { Pagination, Select } from 'antd';
import { MessageSquare, SearchX } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { ListSearch } from '../../../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import type { CollectionFollowUpView } from '../follow-ups.api';
import { FollowUpTimeline } from './FollowUpTimeline';

interface FollowUpsListProps {
  items: CollectionFollowUpView[];
  total: number;
  list: ListQueryControls<FollowUpListQuery>;
  isRefreshing: boolean;
  onCorrect?: (entry: CollectionFollowUpView) => void;
  onRecordPromise?: (entry: CollectionFollowUpView) => void;
}

/** Search, a type filter and the server-paged timeline; the old entries stay up, dimmed, while the next page loads. */
export function FollowUpsList({ items, total, list, isRefreshing, onCorrect, onRecordPromise }: FollowUpsListProps) {
  if (total === 0 && !list.isFiltered) {
    return (
      <EmptyState
        icon={MessageSquare}
        title="No follow-ups yet"
        description="Calls, emails, visits and WhatsApp messages made to collect from this customer appear here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-3">
        <ListSearch value={list.query.q} placeholder="Search outcome, notes or contact" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
        <Select
          mode="multiple"
          allowClear
          className="min-w-56"
          placeholder="Type"
          optionFilterProp="label"
          value={list.query.type ?? []}
          options={FOLLOW_UP_TYPES.map((type) => ({ value: type, label: FOLLOW_UP_TYPE_LABELS[type] }))}
          onChange={(type) => list.setQuery({ type })}
        />
      </div>
      {total === 0 ? (
        <EmptyState icon={SearchX} title="No matching follow-ups" description="No follow-ups match." />
      ) : (
        <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined} aria-busy={isRefreshing}>
          <FollowUpTimeline items={items} onCorrect={onCorrect} onRecordPromise={onRecordPromise} />
        </div>
      )}
      <Pagination
        align="end"
        current={list.query.page}
        pageSize={list.query.pageSize}
        total={total}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
        showSizeChanger
        showTotal={(count) => `${count} total`}
        onChange={(page, pageSize) => list.setQuery({ page, pageSize })}
      />
    </div>
  );
}
